import { startOfDay } from "date-fns";

const modeStorageApiMapping = {
    save: {
        default: 'setItem',
        flatten: 'setFlattened',
        list: 'setList'
    },
    restore: {
        default: 'getItem',
        flatten: 'getFlattened',
        list: 'getList'
    }
};

export function saveHandler(reducedState, storage, singleStorageKey = null, stringify = false, async = false) {
    const results = [];

    Object.keys(reducedState).forEach(mode => {
        const storageMethod = mode in modeStorageApiMapping['save'] ? modeStorageApiMapping['save'][mode] : mode;

        if(!(storageMethod in storage)) {
            throw new Error(`Storage method ${storageMethod} for mode ${mode} not supported`);
        }

        if(singleStorageKey) {
            const saveData = stringify && typeof reducedState[mode] != 'string' ? JSON.stringify(reducedState[mode]) : reducedState[mode];

            results.push(storage[storageMethod](singleStorageKey, saveData));
        } else {
            Object.keys(reducedState[mode]).forEach(storageKey => {
                const saveData = stringify ? JSON.stringify(reducedState[mode][storageKey]) : reducedState[mode][storageKey];

                results.push(storage[storageMethod](storageKey, saveData));
            });
        }
    });

    return async ? Promise.all(results) : results;
}

export function restoreHandler(storage, singleStorageKey = null, modules = null, async = false) {
    if(singleStorageKey) {
        const storageMethod = modeStorageApiMapping['restore']['default'];

        if(!(storageMethod in storage)) {
            throw new Error(`Storage method ${storageMethod} for mode ${mode} not supported`);
        }

        return storage[storageMethod](singleStorageKey);
    } else if('getStores' in storage) {
        const stores = storage.getStores();
        const restoredData = {};
        const restorePromises = [];
        const keyPathMappings = {};

        if(modules) {
            Object.keys(modules).forEach(moduleKey => {
                const module = modules[moduleKey];
                const modulePersistSettings = 'settings' in module && 'persist' in module.settings ? module.settings.persist : null;

                if(modulePersistSettings && 'storageKeyPathMapping' in modulePersistSettings) {
                    Object.entries(modulePersistSettings.storageKeyPathMapping).forEach(([ storageKey, stateTarget ]) => {
                        keyPathMappings[storageKey] = [ moduleKey ].concat(stateTarget);
                    });
                }
            });
        }

        const setRestoredData = (storageKey, data, storeOptions) => {
            let targetPath = storageKey in modules ? [ storageKey ] : null;

            if(storageKey in keyPathMappings) {
                targetPath = keyPathMappings[storageKey];
            }
            
            if(targetPath && (!('restoreInRoot' in storeOptions) || !storeOptions.restoreInRoot)) {
                const lastTargetKey = targetPath.pop();
                let target = restoredData;

                for(const targetPathKey of targetPath) {
                    if(!(targetPathKey in target)) {
                        target[targetPathKey] = {};
                    }

                    target = target[targetPathKey];
                }

                target[lastTargetKey] = data;
            } else if(targetPath) {
                Object.assign(restoredData, data);
            }
        }

        Object.keys(stores).forEach(storageKey => {
            const storeOptions = stores[storageKey];
            const moduleName = 'module' in storeOptions ? storeOptions.module : storageKey;
            const module = storageKey in modules ? modules[moduleName] : {};
            const modulePersistSettings = 'settings' in module && 'persist' in module.settings ? module.settings.persist : {};

            if((!('restore' in storeOptions) || storeOptions.restore) && (!('restore' in modulePersistSettings) || modulePersistSettings.restore)) {
                // only restore current store if 'restore' option is not set or true
                const mode = 'mode' in storeOptions ? storeOptions.mode : 'default';
                const storageMethod = mode in modeStorageApiMapping['restore'] ? modeStorageApiMapping['restore'][mode] : mode;

                if(!(storageMethod in storage)) {
                    throw new Error(`Storage method ${storageMethod} for mode ${mode} not supported`);
                }

                if(async) {
                    const restorePromise = storage[storageMethod](storageKey);
                    
                    restorePromise.then(result => {
                        setRestoredData(storageKey, result, storeOptions);
                    });

                    restorePromises.push(restorePromise);
                } else {
                    setRestoredData(storageKey, storage[storageMethod](storageKey), storeOptions);
                }
            }
        });

        if(async) {
            return new Promise((resolve, reject) => {
                Promise.all(restorePromises).then(() => {
                    resolve(restoredData);
                }).catch(err => {
                    reject(err);
                })
            });
        } else {
            return restoredData;
        }
    } else {
        throw new Error('Storage must implement method "getStores" if no singleStorageKey is provided');
    }
}